import { useGetEmail, useResendEmail, useSearchEmailPeople, useSearchEmails } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Input from '@components/V4/Inputs/Input';
import Modal from '@components/V4/Modal/Modal';
import Pagination from '@components/V4/Pagination';
import Select from '@components/V4/Select/Select';
import { BanIcon, ExclamationCircleIcon, MailIcon, MailOpenIcon } from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import {
  EmailDeliveryStatusDTO,
  EmailLogDetailDTO,
  EmailLogFilters,
  EmailLogPersonDTO,
  EmailLogRowDTO,
  EmailRecipientDTO,
  EmailRecipientRole,
} from '@shared/apis/emailLog.apis';
import useDebounce from 'apps/agora/src/hooks/useDebounce';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

const FIRST_YEAR = 2020;

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow =
  'cursor-pointer border-b border-white/5 align-top transition-colors last:border-b-0 hover:bg-surfaceHover';
const leftHead = 'px-5 py-3 text-left font-semibold';
const leftCell = 'px-5 py-3 text-left text-customLight';

const ROLE_STYLES: Record<EmailRecipientRole, string> = {
  student: 'bg-sky-500/15 text-sky-300',
  parent: 'bg-amber-500/15 text-amber-300',
  mentor: 'bg-emerald-500/15 text-emerald-300',
  staff: 'bg-violet-500/15 text-violet-300',
  internal: 'bg-violet-500/15 text-violet-300',
  unknown: 'bg-white/10 text-customGrey',
};

const ROLE_LABELS: Record<EmailRecipientRole, string> = {
  student: 'Student',
  parent: 'Parent',
  mentor: 'Mentor',
  staff: 'Staff',
  internal: 'Internal',
  unknown: 'Unknown',
};

const MONTH_OPTIONS = moment.months().map((label, index) => ({ label, value: index + 1 }));

const YEAR_OPTIONS = Array.from({ length: moment().year() - FIRST_YEAR + 1 }, (_, index) => {
  const year = moment().year() - index;
  return { label: String(year), value: year };
});

const RECIPIENT_TYPE_OPTIONS = [
  { label: 'Students', value: 'student' },
  { label: 'Parents', value: 'parent' },
  { label: 'Mentors', value: 'mentor' },
  { label: 'Internal (@upgrade.education)', value: 'internal' },
];

const STATUS_OPTIONS = [
  { label: 'Accepted by SendGrid', value: 'sent' },
  { label: 'Failed / not confirmed', value: 'failed' },
  { label: 'Delivered to inbox server', value: 'delivered' },
  { label: 'Bounced / dropped / spam', value: 'bounced' },
  { label: 'Opened', value: 'opened' },
];

const DELIVERY_LABELS: Record<EmailDeliveryStatusDTO, string> = {
  processed: 'Processing',
  deferred: 'Deferred',
  delivered: 'Delivered',
  bounced: 'Bounced',
  dropped: 'Dropped',
  spam: 'Marked as spam',
};

const DELIVERY_STYLES: Record<EmailDeliveryStatusDTO, string> = {
  processed: 'bg-white/10 text-customGrey',
  deferred: 'bg-amber-500/15 text-amber-300',
  delivered: 'bg-emerald-500/15 text-emerald-300',
  bounced: 'bg-red-500/15 text-red-300',
  dropped: 'bg-red-500/15 text-red-300',
  spam: 'bg-red-500/15 text-red-300',
};

const pill = 'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold';

const STUDENT_SCOPE_OPTIONS = [
  { label: 'Student + parent', value: 'both' },
  { label: 'Student only', value: 'student' },
  { label: 'Parent only', value: 'parent' },
];

const formatDate = (value: string) => moment(value).format('DD MMM YYYY, HH:mm');

const RecipientChip = ({ recipient }: { recipient: EmailRecipientDTO }) => (
  <span className="inline-flex max-w-full flex-wrap items-center gap-1.5">
    <span
      className={mergeClassNames(
        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
        ROLE_STYLES[recipient.role]
      )}
    >
      {ROLE_LABELS[recipient.role]}
    </span>
    <span className="break-all text-customLight">
      {recipient.name ? `${recipient.name} · ` : ''}
      <span className="text-customGrey">{recipient.email}</span>
      {recipient.relatedStudent ? (
        <span className="text-customGrey"> (parent of {recipient.relatedStudent})</span>
      ) : null}
    </span>
  </span>
);

const StatusBadge = ({ successful }: { successful: boolean }) => (
  <span
    className={mergeClassNames(
      pill,
      successful ? 'bg-sky-500/15 text-sky-300' : 'bg-red-500/15 text-red-300'
    )}
  >
    {successful ? 'Sent' : 'Failed'}
  </span>
);

const StatusBadges = ({ email }: { email: EmailLogRowDTO }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <StatusBadge successful={email.successful} />
    {email.deliveryStatus ? (
      <span className={mergeClassNames(pill, DELIVERY_STYLES[email.deliveryStatus])}>
        {DELIVERY_LABELS[email.deliveryStatus]}
      </span>
    ) : null}
    {email.openedAt ? (
      <span className={mergeClassNames(pill, 'bg-emerald-500/15 text-emerald-300')}>Opened</span>
    ) : null}
    {email.resentEmailId ? (
      <span className={mergeClassNames(pill, 'bg-violet-500/15 text-violet-300')}>Resent</span>
    ) : null}
    {email.resendOf ? (
      <span className={mergeClassNames(pill, 'bg-violet-500/15 text-violet-300')}>Resend</span>
    ) : null}
  </div>
);

const canResend = (email: EmailLogDetailDTO) =>
  !email.successful ||
  email.deliveryStatus === 'bounced' ||
  email.deliveryStatus === 'dropped' ||
  email.deliveryStatus === 'deferred';

const PersonPicker = (props: {
  personRole: 'student' | 'mentor';
  label: string;
  value?: EmailLogPersonDTO;
  onChange: (person?: EmailLogPersonDTO) => void;
}) => {
  const { personRole, label, value, onChange } = props;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: people, isFetching } = useSearchEmailPeople(
    { search: debouncedSearch, role: personRole },
    { enabled: debouncedSearch.trim().length >= 2 }
  );

  const candidates = useMemo(() => {
    const list = [...(value ? [value] : []), ...(people ?? [])];
    return [...new Map(list.map((person) => [person._id, person])).values()];
  }, [people, value]);

  const options = candidates.map((person) => ({
    value: person._id,
    label: `${person.name || person.email} · ${person.email}${
      person.isDeactivated ? ' (deactivated)' : ''
    }`,
  }));

  return (
    <FilterField width="tablet:w-72">
      <Select
        variant="secondary"
        label={label}
        showSearch
        allowClear
        isLoading={isFetching}
        placeholder={`Search ${personRole} by name or email`}
        value={value?._id}
        options={options}
        filterOption={() => true}
        notFoundContent={
          search.trim().length < 2
            ? 'Type at least 2 characters'
            : isFetching
            ? 'Searching…'
            : 'None'
        }
        onSearch={setSearch}
        onChange={(id) => {
          onChange(candidates.find((person) => person._id === id));
          setSearch('');
        }}
        onClear={() => onChange(undefined)}
      />
    </FilterField>
  );
};

const EmailPreviewModal = (props: {
  emailId?: string;
  onClose: () => void;
  onOpenEmail: (id: string) => void;
}) => {
  const { emailId, onClose, onOpenEmail } = props;
  const toast = useToast();
  const { data: email, isLoading } = useGetEmail(emailId);
  const [view, setView] = useState<'html' | 'text'>('html');
  const [isConfirmingResend, setIsConfirmingResend] = useState(false);

  const { mutate: resend, isLoading: isResending } = useResendEmail(emailId ?? '', {
    onSuccess: (resent) => {
      toast.success('The email was sent again.');
      setIsConfirmingResend(false);
      onOpenEmail(resent._id);
    },
    onError: (error) => {
      const message = (error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      toast.error(message ?? 'The email could not be resent.');
      setIsConfirmingResend(false);
    },
  });

  useEffect(() => {
    setView('html');
    setIsConfirmingResend(false);
  }, [emailId]);

  const hasHtml = !!email?.html?.trim();
  const shownView = hasHtml ? view : 'text';

  return (
    <Modal isOpen={!!emailId} onClose={onClose} size="large">
      <Modal.Header title={email?.subject || 'Email'} />
      <Modal.Body className="flex flex-col gap-4 text-sm text-white">
        {isLoading || !email ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 rounded-xl bg-surfaceObject p-4 tablet:grid-cols-[120px_1fr]">
              <dt className="text-customGrey">Status</dt>
              <dd className="flex flex-col gap-1">
                <StatusBadges email={email} />
                {!email.successful ? (
                  <span className="text-red-300">
                    {email.error || 'SendGrid did not confirm this send. No error was recorded.'}
                  </span>
                ) : null}
                {email.deliveryReason ? (
                  <span className="text-customGrey">{email.deliveryReason}</span>
                ) : null}
                {email.openedAt ? (
                  <span className="text-customGrey">
                    First opened {formatDate(email.openedAt)}
                    {email.clickedAt ? `, first click ${formatDate(email.clickedAt)}` : ''}
                  </span>
                ) : null}
              </dd>
              <dt className="text-customGrey">Created</dt>
              <dd>{formatDate(email.createdAt)}</dd>
              <dt className="text-customGrey">From</dt>
              <dd className="break-all">{email.from || '—'}</dd>
              <dt className="text-customGrey">To</dt>
              <dd className="flex flex-col gap-1">
                {email.to.length
                  ? email.to.map((recipient) => (
                      <RecipientChip key={recipient.email} recipient={recipient} />
                    ))
                  : '—'}
              </dd>
              {email.cc.length ? (
                <>
                  <dt className="text-customGrey">Cc</dt>
                  <dd className="flex flex-col gap-1">
                    {email.cc.map((recipient) => (
                      <RecipientChip key={recipient.email} recipient={recipient} />
                    ))}
                  </dd>
                </>
              ) : null}
              {email.attachmentNames.length ? (
                <>
                  <dt className="text-customGrey">Attachments</dt>
                  <dd>{email.attachmentNames.join(', ')}</dd>
                </>
              ) : null}
              {email.resendOf ? (
                <>
                  <dt className="text-customGrey">Resend of</dt>
                  <dd>
                    <button
                      type="button"
                      className="text-customPrimary"
                      onClick={() => onOpenEmail(email.resendOf as string)}
                    >
                      Open the original email
                    </button>
                  </dd>
                </>
              ) : null}
              {email.resentEmailId ? (
                <>
                  <dt className="text-customGrey">Resent</dt>
                  <dd className="flex flex-wrap items-center gap-2">
                    <span>
                      {email.resentAt ? formatDate(email.resentAt) : ''}
                      {email.resentByName ? ` by ${email.resentByName}` : ''}
                    </span>
                    <button
                      type="button"
                      className="text-customPrimary"
                      onClick={() => onOpenEmail(email.resentEmailId as string)}
                    >
                      Open the new email
                    </button>
                  </dd>
                </>
              ) : null}
              {email.sendgridMessageId ? (
                <>
                  <dt className="text-customGrey">SendGrid ID</dt>
                  <dd className="break-all font-mono text-xs">{email.sendgridMessageId}</dd>
                </>
              ) : null}
            </dl>

            {email.deliveryEvents.length ? (
              <details className="rounded-xl bg-surfaceObject p-4">
                <summary className="cursor-pointer text-customGrey">
                  SendGrid events ({email.deliveryEvents.length})
                </summary>
                <ul className="mt-3 flex flex-col gap-1.5">
                  {email.deliveryEvents.map((event, index) => (
                    <li key={`${event.event}-${event.timestamp}-${index}`} className="flex gap-3">
                      <span className="w-40 shrink-0 text-customGrey">
                        {formatDate(event.timestamp)}
                      </span>
                      <span className="w-24 shrink-0 font-semibold">{event.event}</span>
                      <span className="break-all text-customGrey">{event.reason ?? ''}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {canResend(email) && !email.resentEmailId ? (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 p-4">
                {isConfirmingResend ? (
                  <>
                    <span className="flex-1 text-customLight">
                      Send this exact email again to the same recipients?
                      {email.attachmentNames.length
                        ? ` The attachments (${email.attachmentNames.join(
                            ', '
                          )}) were not stored and will be missing.`
                        : ''}
                    </span>
                    <Button
                      variant="secondary"
                      buttonText="Cancel"
                      disabled={isResending}
                      onClick={() => setIsConfirmingResend(false)}
                    />
                    <Button
                      buttonText="Send again"
                      isLoading={isResending}
                      onClick={() => resend({})}
                    />
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-customGrey">
                      This email did not reach the recipient. You can send it again exactly as it
                      was.
                    </span>
                    <Button buttonText="Resend email" onClick={() => setIsConfirmingResend(true)} />
                  </>
                )}
              </div>
            ) : null}

            <div className="flex gap-2">
              {(['html', 'text'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={option === 'html' && !hasHtml}
                  className={mergeClassNames(
                    'rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40',
                    shownView === option
                      ? 'bg-customPrimary text-black'
                      : 'bg-surfaceObject text-customGrey hover:bg-surfaceHover'
                  )}
                  onClick={() => setView(option)}
                >
                  {option === 'html' ? 'Rendered' : 'Plain text'}
                </button>
              ))}
            </div>

            {shownView === 'html' ? (
              <iframe
                title="Email preview"
                srcDoc={email.html}
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                className="!block h-[65vh] w-full rounded-xl border-0 bg-white"
              />
            ) : (
              <pre className="max-h-[65vh] overflow-auto whitespace-pre-wrap rounded-xl bg-surfaceObject p-4 font-sans text-sm text-customLight">
                {email.text?.trim() || 'This email has no plain-text version.'}
              </pre>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

const EmailLog = () => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [student, setStudent] = useState<EmailLogPersonDTO>();
  const [mentor, setMentor] = useState<EmailLogPersonDTO>();
  const [filters, setFilters] = useState<EmailLogFilters>({ studentScope: 'both' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selectedEmailId, setSelectedEmailId] = useState<string>();

  const updateFilters = (patch: Partial<EmailLogFilters>) => {
    setFilters((previous) => ({ ...previous, ...patch }));
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, student, mentor]);

  const params: EmailLogFilters = {
    ...filters,
    search: debouncedSearch.trim() || undefined,
    studentId: student?._id,
    studentScope: student ? filters.studentScope : undefined,
    mentorId: mentor?._id,
    month: filters.year ? filters.month : undefined,
    page,
    limit,
  };

  const { data, isLoading, isFetching } = useSearchEmails(params, { keepPreviousData: true });

  const total = data?.total ?? 0;
  const failed = data?.failed ?? 0;
  const bounced = data?.bounced ?? 0;
  const successRate = total ? `${Math.round(((total - failed) / total) * 1000) / 10}%` : 'n/a';

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Email Log</h1>
        <p className="max-w-3xl text-sm text-customGrey">
          Every email the platform has sent through SendGrid, newest first. Recipients are matched
          to Agora users by address. Click a row to see the email exactly as it was sent.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterField width="tablet:w-72">
          <Input
            type="text"
            name="email-log-search"
            id="email-log-search"
            label="Search"
            placeholder="Subject or email address"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </FilterField>
        <PersonPicker personRole="student" label="Student" value={student} onChange={setStudent} />
        <FilterField width="tablet:w-48">
          <Select
            variant="secondary"
            label="Student emails"
            allowClear={false}
            isDisabled={!student}
            value={filters.studentScope}
            options={STUDENT_SCOPE_OPTIONS}
            onChange={(value) =>
              updateFilters({ studentScope: value as EmailLogFilters['studentScope'] })
            }
          />
        </FilterField>
        <PersonPicker personRole="mentor" label="Mentor" value={mentor} onChange={setMentor} />
        <FilterField width="tablet:w-56">
          <Select
            variant="secondary"
            label="Recipient type"
            placeholder="Anyone"
            value={filters.recipientType}
            options={RECIPIENT_TYPE_OPTIONS}
            onChange={(value) =>
              updateFilters({
                recipientType: (value ?? undefined) as EmailLogFilters['recipientType'],
              })
            }
          />
        </FilterField>
        <FilterField width="tablet:w-52">
          <Select
            variant="secondary"
            label="Status"
            placeholder="Any status"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={(value) =>
              updateFilters({ status: (value ?? undefined) as EmailLogFilters['status'] })
            }
          />
        </FilterField>
        <FilterField width="tablet:w-32">
          <Select
            variant="secondary"
            label="Year"
            placeholder="Any"
            value={filters.year}
            options={YEAR_OPTIONS}
            onChange={(value) => updateFilters({ year: (value as number) ?? undefined })}
          />
        </FilterField>
        <FilterField width="tablet:w-40">
          <Select
            variant="secondary"
            label="Month"
            placeholder="Any"
            isDisabled={!filters.year}
            value={filters.year ? filters.month : undefined}
            options={MONTH_OPTIONS}
            onChange={(value) => updateFilters({ month: (value as number) ?? undefined })}
          />
        </FilterField>
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-4">
        <MetricCard
          label="Emails matching"
          value={total}
          icon={<MailIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="Failed or not confirmed"
          value={failed}
          icon={<ExclamationCircleIcon className="h-5 w-5" />}
          accent="gold"
          onClick={
            failed && filters.status !== 'failed'
              ? () => updateFilters({ status: 'failed' })
              : undefined
          }
        />
        <MetricCard
          label="Bounced, dropped or spam"
          value={bounced}
          icon={<BanIcon className="h-5 w-5" />}
          accent="lila"
          onClick={
            bounced && filters.status !== 'bounced'
              ? () => updateFilters({ status: 'bounced' })
              : undefined
          }
        />
        <MetricCard
          label="Accepted by SendGrid"
          value={successRate}
          icon={<MailOpenIcon className="h-5 w-5" />}
          accent="green"
        />
      </div>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title="Emails"
          description="“Failed” means SendGrid rejected the send or never confirmed it. Delivered, bounced and opened come from SendGrid's event webhook, so they exist only for emails sent after it was switched on."
        />

        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : !data?.emails.length ? (
          <EmptyState>No emails match these filters.</EmptyState>
        ) : (
          <div className={mergeClassNames('flex flex-col gap-4', isFetching ? 'opacity-60' : '')}>
            <TableShell minWidth="min-w-[960px]">
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Date</th>
                  <th className={leftHead}>Subject</th>
                  <th className={leftHead}>Recipients</th>
                  <th className={leftHead}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.emails.map((email) => (
                  <tr
                    key={email._id}
                    className={bodyRow}
                    tabIndex={0}
                    onClick={() => setSelectedEmailId(email._id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') setSelectedEmailId(email._id);
                    }}
                  >
                    <td className={mergeClassNames(leftCell, 'whitespace-nowrap')}>
                      {formatDate(email.createdAt)}
                    </td>
                    <td className={mergeClassNames(leftCell, 'max-w-[320px]')}>
                      {email.subject || <span className="text-customGrey">(no subject)</span>}
                    </td>
                    <td className="px-5 py-3 text-left">
                      <div className="flex flex-col gap-1">
                        {[...email.to, ...email.cc].map((recipient, index) => (
                          <RecipientChip
                            key={`${recipient.email}-${index}`}
                            recipient={recipient}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-left">
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadges email={email} />
                        {email.error ? (
                          <span className="line-clamp-2 max-w-[220px] text-xs text-red-300">
                            {email.error}
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableShell>

            <Pagination
              pageSize={limit}
              currentPage={page}
              total={total}
              onCurrentPageChange={setPage}
              onPageSizeChange={(size) => {
                setLimit(size);
                setPage(1);
              }}
            />
          </div>
        )}
      </section>

      <EmailPreviewModal
        emailId={selectedEmailId}
        onClose={() => setSelectedEmailId(undefined)}
        onOpenEmail={setSelectedEmailId}
      />
    </div>
  );
};

export default EmailLog;
